<script lang="ts" setup>
// TODO ADD UNIT TESTS TO THIS COMPONENT
import { computed, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { useSettingsStore } from '../stores/settings';
import { useMeStore } from '~/stores/me';

import { useTranslations } from '~/composables/translations';

import LanguageSelector from './LanguageSelector.vue';
const vuDropdownmenu = defineAsyncComponent(() => import('@v/components/vu-dropdownmenu.vue'));
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import { router } from '~/router';
import clientOnly from '~/modules/client-only';

const route = useRoute();
const me = useMeStore();
const settings = useSettingsStore();

const { $i18n } = useTranslations('navbar');

const currentRoute = computed(() => route.name);
const isInOrdering = computed(() => route.path.includes('/quote'));
const isInLabManagement = computed(() => route.path.includes('/lab'));
const items = computed(() =>
  [
    {
      id: 'Home',
      label: $i18n('home'),
      icon: 'home',
      to: '/welcome',
      display: true,
      onlyIcon: true,
    },
    {
      id: 'QuoteUploadPart',
      label: $i18n('request'),
      to: '/quote/upload',
      display: !me.isOperator,
    },
    {
      id: 'ContactSales',
      label: $i18n('contactSales'),
      to: 'https://www.3ds.com/3dexperience/make/3dexperience-make-contact-sales',
      isExternal: true,
      display: !me.isOperator,
    },
    {
      id: 'LabsExploration',
      label: $i18n('explore'),
      to: '/labs',
      display: !me.isOperator,
    },
    {
      id: 'MyPrints',
      label: $i18n('myRequests'),
      to: '/my-prints',
      display: me.isOperator ? false : me.isLoggedIn,
    },
    {
      id: 'LabOverview',
      label: $i18n('myService'),
      tabs: [
        'MyOrders',
        'SellerStats',
        'LabManagement',
        'LabManagementInfo',
        'LabManagementFinishes',
        'LabManagementServices',
        'LabManagementPricing',
        'LabManagementPricingAdditive',
        'LabManagementPricingFinish',
        'LabManagementShipping',
        'LabManagementShowroom',
        'LabManagementMembers',
        'LabManagementPayment',
        'LabManagementSubmit',
        'LabManagementSubmitSuccess',
        'LabManagementNewMachine',
        'LabManagementMachines',
        'LabManagementMachine',
      ],
      to: { name: 'LabOverview', params: { id: me.currentShop } },
      display: me.isSeller && me.currentShop,
    },
    {
      id: 'LabCreation',
      label: $i18n('myService'),
      to: { name: 'LabCreation' },
      display: me.isSeller && !me.currentShop,
    },
    {
      id: 'LabValidation',
      label: $i18n('labValidation'),
      to: '/lab-validation',
      tabs: ['LabValidation'],
      display: me.isOperator,
    },
    {
      id: 'TermsAndConditions',
      label: $i18n('termsAndConditions'),
      to: '/terms-and-conditions',
      tabs: ['TermsAndConds'],
      display: me.isOperator,
    },
    {
      id: 'ReferentialAdmin',
      label: $i18n('referentialAdmin'),
      to: '/referential-admin',
      tabs: ['ReferentialAdmin'],
      display: me.isOperator,
    },
    {
      id: 'SearchOrders',
      label: $i18n('searchOrders'),
      to: '/search-orders',
      display: me.isOperator,
    },
  ].filter((el) => el && el.display),
);

const isItemSelected = (item: any) =>
  currentRoute.value === item.id || (item.tabs ? item.tabs.includes(currentRoute.value) : false);
const shops = computed(() => me.shops?.map((el: any) => ({ label: el.shopName, value: el.id })));
const clickItem = (item: any) => {
  if (item.isExternal) {
    window.open(item.to);
    return;
  }

  router.push(item.to);
};

const changeSeller = async ({ value }: { value: string }) => {
  me.setCurrentShop(value);
  router.push({ name: route.name as string, params: { id: value } });
};
</script>
<template>
  <nav
    id="navbar"
    :class="[
      'slide-left-enter flex flex-row align-items-center grid-gap-10px px-15px mp-nav-bar',
      { 'justify-end': !settings.isMobileDevice || isInOrdering },
    ]"
  >
    <clientOnly>
      <template v-if="!settings.isMobileDevice || isInOrdering">
        <span class="loose-focus" />
        <ul class="flex align-items-center justify-end grid-gap-20px">
          <template v-if="!isInOrdering">
            <li v-for="item in items" :key="`nav-${item}`">
              <template v-if="item.isExternal && item.display">
                <a
                  :href="item.to"
                  target="_blank"
                  class="flex align-items-center"
                  :aria-label="item.label"
                >
                  {{ item.label }}
                </a>
              </template>
              <template v-else-if="item.onlyIcon">
                <router-link
                  :to="item.to"
                  :class="['flex align-items-center', { active: isItemSelected(item) }]"
                  :id="`navbar-item-${item.id}`"
                  :aria-label="item.label"
                >
                  <span :class="`fonticon fonticon-${item.icon}`" />
                </router-link>
              </template>
              <template v-else>
                <router-link
                  :to="item.to"
                  :class="['flex align-items-center', { active: isItemSelected(item) }]"
                  :id="`navbar-item-${item.id}`"
                  :aria-label="item.label"
                >
                  {{ item.label }}
                </router-link>
              </template>
            </li>
          </template>
          <template v-else>
            <li>
              <router-link
                :to="'/welcome'"
                class="flex align-items-center grid-gap-5px"
                id="navbar-item-BackToHome"
              >
                <span class="fonticon fonticon-home" />
                {{ $i18n('backToHome') }}
              </router-link>
            </li>
            <li v-if="me.isOperator ? false : me.isLoggedIn">
              <router-link
                to="/my-prints"
                class="flex align-items-center"
                id="navbar-item-MyPrints"
                :aria-label="$i18n('myRequests')"
              >
                {{ $i18n('myRequests') }}
              </router-link>
            </li>
          </template>
        </ul>
      </template>
      <template v-if="settings.isMobileDevice && !isInOrdering">
        <vuDropdownmenu
          :items="items.map((el) => ({ ...el, class: isItemSelected(el) ? 'active' : '' }))"
          @click-item="clickItem"
          :overlay="settings.isMobileDevice"
        >
          <span class="fonticon fonticon-menu pointer" />
        </vuDropdownmenu>
        <vuDropdownmenu
          :items="shops"
          @click-item="changeSeller"
          v-if="isInLabManagement && shops.length && shops.length > 1"
        >
          <div class="pointer flex flex-row grid-gap-5px align-items-center fade-keyframe">
            {{ shops.find((el) => el.value === me.currentShop)?.label }}
            <vu-icon icon="expand-down" size="10" />
          </div>
        </vuDropdownmenu>
      </template>
    </clientOnly>
    <LanguageSelector
      :class="['justify-end', { 'flex-grow-1': settings.isMobileDevice && !isInOrdering }]"
    />
  </nav>
</template>
<style scoped lang="css">
@keyframes expand-to-full-width {
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
}

#navbar {
  height: 44px;
  width: 100%;
  border-bottom: 1px solid #e0e0e0;
  font-family: '3ds';
  background-color: #fff;
}

ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
  height: 44px;
}

li {
  height: 44px;
}

a {
  position: relative;
  padding-left: 10px;
  padding-right: 10px;
  color: #77797c;
  text-decoration: none;
  font-size: 16px;
  line-height: 17px;
  color: #368ec4;
  height: 44px;
  font-family: '3ds';
}

a:active {
  text-decoration: none;
}
a:hover {
  text-decoration: none;
}
a:not(.active):hover:before {
  content: ' ';
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  width: 100%;
  background: #77797c !important;
  animation: expand-to-full-width 0.3s ease-out forwards;
}
a:focus {
  text-decoration: none;
}

.fonticon-home {
  color: black;
}

#navbar a.active::before {
  content: ' ';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: #368ec4 !important;
}

#navbar a.active {
  height: 44px;
}

.loose-focus {
  position: absolute;
  left: 0;
  top: 0;
  width: 10px;
  height: 10px;
}
</style>
<style>
.dropdown-menu-wrap .active {
  border-left: 5px solid #368ec4 !important;
  background: #f4f5f6;
}
</style>
